import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, SxProps, Tab, Theme } from '@mui/material';
import { useSnackbar } from 'notistack';

import { currentTaskFormApi } from '../../axios/currentTaskForm';
import { useLanguage } from '../../hooks';
import { Edit2Outline, EmailOutline, FileOutline, FilePdf, Plug } from '../../icons';
import { datascopeTracker } from '../../utils/eventTracker';
import { FormBuilder } from '../FormBuilder/formBuilder';
import { FormBuilderProvider } from '../FormBuilder/FormBuilderProvider';
import { FormEmailBuilder } from '../FormEmailBuilder/FormEmailBuilder';
import { FormSignatureBuilder } from '../FormSignatureBuilder/FormSignatureBuilder';
import { Iframe } from '../uiComponents/CommonComponents/Iframe';
import { Loader } from '../uiComponents/CommonComponents/Loader';

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
  main: {
    flex: 1,
  },
  tabsContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 95,
  },
  tabs: {
    '&.MuiTabs-root': {
      background: '#FFFFFF',
      minHeight: '42px',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.09)',
      padding: '0px 14px',
      borderBottom: '1px solid #F3F5F9',
    },
    '& .MuiTab-root': {
      paddingTop: '2px',
      paddingBottom: '2px',
      minHeight: '42px',
      textTransform: 'none',
      fontSize: '15px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
      '& .MuiSvgIcon-root': {
        fontSize: '18px',
        marginBottom: '0px',
      },
    },
    '& .MuiTabs-indicator': {
      height: '3px',
      borderRadius: '20px',
    },
  },
  tabPanel: {
    padding: '0px',
    height: '100%',
    width: '100%',
  },
};

export enum FormBuilderTabs {
  BUILDER,
  INTEGRATIONS,
  SIGNATURE,
  EMAIL,
  PDF,
}

export function FormBuilderLayout() {
  const lang = useLanguage();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { formId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get('tab') || '0';
  const [isCheckingFormLockStatus, setIsCheckingFormLockStatus] = useState(true);

  useEffect(() => {
    const checkFormLockStatus = async () => {
      if (!formId || formId === 'new') {
        setIsCheckingFormLockStatus(false);
        return;
      }

      try {
        const data = await currentTaskFormApi.getTaskFormEditingLockStatus(parseInt(formId));
        if (data.updates_locked_at) {
          enqueueSnackbar(lang.form_builder.formLockedForEditing, {
            variant: 'error',
          });
          navigate('/forms');
          return;
        }
      } catch (error) {
        console.error('Error checking form lock status:', error);
      } finally {
        setIsCheckingFormLockStatus(false);
      }
    };

    checkFormLockStatus();
  }, [navigate]);

  if (isCheckingFormLockStatus) {
    return <Loader />;
  }

  const handleClickOnSignatureSettingsTab = () => {
    datascopeTracker(
      'View signature settings',
      {
        form_id: formId,
      },
      'AMP',
    );
    setSearchParams({ tab: `${FormBuilderTabs.SIGNATURE}` });
  };

  return (
    <Box sx={customStyles.root}>
      <TabContext value={selectedTab}>
        <Box sx={customStyles.tabsContainer}>
          <TabList sx={customStyles.tabs}>
            <Tab
              label={lang.form_builder.tabs.builder}
              value={`${FormBuilderTabs.BUILDER}`}
              icon={
                <FileOutline
                  fontSize='inherit'
                  color='inherit'
                />
              }
              onClick={() => setSearchParams({ tab: `${FormBuilderTabs.BUILDER}` })}
            />
            {formId &&
              formId !== 'new' && [
                <Tab
                  key={`${FormBuilderTabs.INTEGRATIONS}`}
                  label={lang.form_builder.tabs.integrations}
                  value={`${FormBuilderTabs.INTEGRATIONS}`}
                  icon={
                    <Plug
                      fontSize='inherit'
                      color='inherit'
                    />
                  }
                  onClick={() => setSearchParams({ tab: `${FormBuilderTabs.INTEGRATIONS}` })}
                />,
                <Tab
                  key={`${FormBuilderTabs.SIGNATURE}`}
                  label={lang.form_builder.tabs.signature}
                  value={`${FormBuilderTabs.SIGNATURE}`}
                  icon={
                    <Edit2Outline
                      fontSize='inherit'
                      color='inherit'
                    />
                  }
                  onClick={handleClickOnSignatureSettingsTab}
                />,
                <Tab
                  key={`${FormBuilderTabs.EMAIL}`}
                  label={lang.form_builder.tabs.email}
                  value={`${FormBuilderTabs.EMAIL}`}
                  icon={
                    <EmailOutline
                      fontSize='inherit'
                      color='inherit'
                    />
                  }
                  onClick={() => setSearchParams({ tab: `${FormBuilderTabs.EMAIL}` })}
                />,
                <Tab
                  key={`${FormBuilderTabs.PDF}`}
                  label={lang.form_builder.tabs.pdf}
                  value={`${FormBuilderTabs.PDF}`}
                  icon={
                    <FilePdf
                      fontSize='inherit'
                      color='inherit'
                    />
                  }
                  onClick={() => setSearchParams({ tab: `${FormBuilderTabs.PDF}` })}
                />,
              ]}
          </TabList>
        </Box>
        <Box sx={customStyles.main}>
          <TabPanel
            value={`${FormBuilderTabs.BUILDER}`}
            sx={customStyles.tabPanel}
          >
            <FormBuilderProvider>
              <FormBuilder />
            </FormBuilderProvider>
          </TabPanel>
          <TabPanel
            value={`${FormBuilderTabs.INTEGRATIONS}`}
            sx={customStyles.tabPanel}
          >
            <Iframe
              url={`task_forms/${formId}/edit`}
              params={{
                edit_type: 'integrate',
              }}
            />
          </TabPanel>
          <TabPanel
            value={`${FormBuilderTabs.SIGNATURE}`}
            sx={customStyles.tabPanel}
          >
            <FormSignatureBuilder />
          </TabPanel>
          <TabPanel
            value={`${FormBuilderTabs.EMAIL}`}
            sx={customStyles.tabPanel}
          >
            <FormEmailBuilder />
          </TabPanel>
          <TabPanel
            value={`${FormBuilderTabs.PDF}`}
            sx={customStyles.tabPanel}
          >
            <Iframe
              url={`task_forms/${formId}/edit`}
              params={{
                edit_type: 'pdf',
              }}
            />
          </TabPanel>
        </Box>
      </TabContext>
    </Box>
  );
}
